import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, limit, orderBy, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import { FiArrowLeft } from 'react-icons/fi';
import { FaWhatsapp, FaLinkedin, FaTwitter, FaInstagram } from 'react-icons/fa';
import LazyImage from '../../components/ui/LazyImage';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPostBySlug();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchPostBySlug = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'blog_posts'),
        where('slug', '==', slug),
        where('status', '==', 'published'),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const postData = { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
        setPost(postData);
        
        // SEO: Atualizar meta tags (Solução client-side básica)
        document.title = `${postData.metaTitle || postData.title} | Blog CriaApp`;
        
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          metaDesc.setAttribute('content', postData.metaDescription || postData.excerpt || '');
        } else {
          metaDesc = document.createElement('meta');
          metaDesc.name = "description";
          metaDesc.content = postData.metaDescription || postData.excerpt || '';
          document.head.appendChild(metaDesc);
        }

        // Increment views
        try {
          const postRef = doc(db, 'blog_posts', postData.id);
          await updateDoc(postRef, {
            views: increment(1)
          });
        } catch (err) {
          console.error("Erro ao incrementar views:", err);
        }

        // Buscar relacionados
        if (postData.categoryId) {
          fetchRelatedPosts(postData.categoryId, postData.id);
        }
      } else {
        // Se não encontrar, redireciona ou mostra erro
        navigate('/blog');
      }
    } catch (error) {
      console.error("Erro ao buscar post:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async (categoryId, currentPostId) => {
    try {
      // Buscar posts ordenados e filtrar em memória para evitar erros de Índice no Firebase
      const q = query(
        collection(db, 'blog_posts'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      let postsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      postsList = postsList.filter(p => 
        p.status === 'published' && 
        p.categoryId === categoryId && 
        p.id !== currentPostId
      ).slice(0, 3);
      
      setRelatedPosts(postsList);
    } catch (error) {
      console.error("Erro ao buscar posts relacionados:", error);
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post ? post.title : 'Blog CriaApp';

    switch(platform) {
      case 'whatsapp':
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' - ' + url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'instagram':
        if (navigator.share) {
          navigator.share({
            title: title,
            url: url
          }).catch(console.error);
        } else {
          navigator.clipboard.writeText(url);
          alert('Link copiado! Agora você pode colar no Instagram (Stories ou Bio).');
        }
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-4xl mx-auto mb-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium">
            <FiArrowLeft className="text-lg relative -top-[1px]" /> <span className="pt-[2px]">Voltar para o Blog</span>
          </Link>
        </div>
        <main className="flex-grow flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </main>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-4xl mx-auto mb-6">
        <Link to="/blog" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium">
          <FiArrowLeft className="text-lg relative -top-[1px]" /> <span className="pt-[2px]">Voltar para o Blog</span>
        </Link>
      </div>
      
      <main className="flex-grow pb-12 sm:pb-16 lg:pb-24">
        <article className="container mx-auto max-w-4xl">
          
          {/* Header do Post */}
          <header className="mb-8 sm:mb-10 text-center">
            {post.categoryName && (
              <span className="inline-block bg-blue-50 text-blue-600 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-4 sm:mb-6">
                {post.categoryName}
              </span>
            )}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 leading-tight break-words px-2">
              {post.title}
            </h1>
            
            <div className="flex items-center justify-center gap-4 text-gray-600 mb-8">
              <div className="flex items-center gap-3">
                {post.authorAvatar ? (
                  <LazyImage src={post.authorAvatar} alt={post.authorName} containerClassName="w-12 h-12 rounded-full shadow-sm" className="w-full h-full rounded-full object-cover absolute inset-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-lg">
                    {post.authorName?.charAt(0) || 'A'}
                  </div>
                )}
                <div className="text-left">
                  <p className="font-semibold text-gray-900 leading-tight">{post.authorName || 'Autor'}</p>
                  <p className="text-sm">
                    {new Date(post.createdAt?.seconds * 1000).toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
            
            {post.coverImage && (
              <div className="w-full flex justify-center bg-gray-100 rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm mb-8 sm:mb-10 relative" style={{ minHeight: '300px' }}>
                <LazyImage 
                  src={post.coverImage} 
                  alt={post.title}
                  containerClassName="w-full h-full min-h-[300px] flex justify-center" 
                  className="max-w-full w-auto h-auto max-h-[600px] object-contain absolute inset-0 m-auto"
                />
              </div>
            )}
          </header>

          {/* Compartilhamento Topo */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-10 pb-6 border-b border-gray-100">
            <span className="text-gray-500 font-medium text-sm uppercase tracking-wider">Compartilhe:</span>
            <div className="flex gap-3">
              <button onClick={() => handleShare('whatsapp')} className="p-3 bg-green-100 text-green-600 hover:bg-green-600 hover:text-white rounded-full transition-colors" title="WhatsApp">
                <FaWhatsapp size={20} />
              </button>
              <button onClick={() => handleShare('linkedin')} className="p-3 bg-blue-100 text-blue-700 hover:bg-blue-700 hover:text-white rounded-full transition-colors" title="LinkedIn">
                <FaLinkedin size={20} />
              </button>
              <button onClick={() => handleShare('twitter')} className="p-3 bg-sky-100 text-sky-500 hover:bg-sky-500 hover:text-white rounded-full transition-colors" title="Twitter / X">
                <FaTwitter size={20} />
              </button>
              <button onClick={() => handleShare('instagram')} className="p-3 bg-pink-100 text-pink-600 hover:bg-gradient-to-tr hover:from-yellow-400 hover:via-pink-500 hover:to-purple-600 hover:text-white rounded-full transition-all" title="Instagram">
                <FaInstagram size={20} />
              </button>
            </div>
          </div>

          {/* Conteúdo */}
          <div 
            className="prose prose-sm sm:prose-base lg:prose-lg prose-blue max-w-none mb-12 sm:mb-16 text-gray-800 bg-white p-6 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 overflow-hidden break-words whitespace-pre-wrap leading-relaxed"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-16 px-4">
              <span className="text-gray-500 font-medium">Tags:</span>
              {post.tags.map((tag, idx) => (
                <span key={idx} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-sm">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Artigos Relacionados */}
          {relatedPosts.length > 0 && (
            <section className="mb-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center md:text-left">Artigos Relacionados</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map(related => (
                  <Link to={`/blog/${related.slug}`} key={related.id} className="group">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col">
                      {related.coverImage && (
                        <div className="h-32 overflow-hidden relative">
                          <LazyImage 
                            src={related.coverImage} 
                            alt={related.title} 
                            containerClassName="w-full h-full"
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 absolute inset-0"
                          />
                        </div>
                      )}
                      <div className="p-5 flex-grow flex flex-col">
                        <h4 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
                          {related.title}
                        </h4>
                        <span className="text-gray-400 text-xs mt-auto">
                          {new Date(related.createdAt?.seconds * 1000).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

        </article>
      </main>
    </div>
  );
};

export default BlogPost;
